// <renewal-strip> loader. The three.js scene (renewal-strip-scene.js + ~180 kB of three.js from
// jsDelivr) is only fetched after the page has fully loaded and the strip is near the viewport, so it
// never competes with the banner for bandwidth. Works with the theme editor: re-rendered sections
// disconnect (scene disposed) and reconnect (scene mounted again).
if (!customElements.get('renewal-strip')) {
  const hasWebGL = (() => {
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) {
      return false;
    }
  })();

  const pageReady = new Promise((resolve) => {
    if (document.readyState === 'complete') resolve();
    else window.addEventListener('load', resolve, { once: true });
  }).then(
    () => new Promise((resolve) => ('requestIdleCallback' in window ? requestIdleCallback(resolve, { timeout: 1500 }) : setTimeout(resolve, 200)))
  );

  customElements.define(
    'renewal-strip',
    class RenewalStrip extends HTMLElement {
      connectedCallback() {
        this.generation = (this.generation || 0) + 1;
        if (!hasWebGL) {
          this.classList.add('is-fallback');
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '300px 0px' }
        );
        this.observer.observe(this);
      }

      async load() {
        // a remove/re-add while loading bumps the generation, so a stale load never mounts twice
        const generation = this.generation;
        const stale = () => !this.isConnected || generation !== this.generation || this.unmount;
        await pageReady;
        if (stale()) return;
        try {
          const { mount } = await import(this.dataset.scene);
          if (stale()) return;
          const config = JSON.parse(this.querySelector('[data-config]')?.textContent || '{}');
          this.unmount = mount(this, config);
          requestAnimationFrame(() => this.classList.add('is-ready'));
        } catch (e) {
          console.warn('[renewal-strip]', e);
          this.classList.add('is-fallback');
        }
      }

      disconnectedCallback() {
        this.generation = (this.generation || 0) + 1;
        this.observer?.disconnect();
        this.unmount?.();
        this.unmount = null;
        this.classList.remove('is-ready');
      }
    }
  );
}
